import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@/v2/components/common/Typography'
import validator from '@/v2/components/forms/Appointment/validator'
import formatBody from '@/v2/components/forms/Appointment/fromForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import BaseForm from '@/v2/modals/appointment/AppointmentEdit/BaseForm'
import { useLoadData } from '@/v2/modals/appointment/AppointmentEdit/hooks/useLoadData'
import { useSaveNote } from '@/v2/modals/appointment/AppointmentEdit/hooks/useSaveNote'
import RTAppointments from '@/v2/hooks/subscriptions/messages/RTAppointments'
import {
  useDeleteAppointment,
  useUpdateAppointment,
} from '@/v2/hooks/useAppointment'
import { Container, Header, Content, Footer } from './styles'

const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}

const appointmentState = {
  patientType: 'registered',
  registeredPatient: {},
  doctorId: '',
  typeId: null,
  roomId: '',
  startsAtDate: null,
  startsAtTime: null,
  duration: '',
}

const MobileAppointmentEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    loading,
    error,
    types,
    rooms,
    doctors,
    appointment,
    patient,
  } = useLoadData(id)
  const { saveNote } = useSaveNote({ appointmentId: appointment?.id })
  const { updateAppointment } = useUpdateAppointment({ appointmentId: id })
  const { deleteAppointment } = useDeleteAppointment({ appointmentId: id })

  const initialValues = useMemo(() => {
    if (id) {
      return { ...appointment }
    }
    return appointmentState
  }, [appointment, id])

  const [values, setValues] = useState(initialValues)
  const [notes, setNotes] = useState([])
  const [formError, setFormError] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setIsLoading(loading)
  }, [loading])

  useEffect(() => {
    if (appointment) {
      setValues(initialValues)
    }
  }, [appointment, initialValues])

  const filteredTypes = isLoading
    ? types
    : types.filter(type => type.isRemote === appointment?.virtual)

  const handleBack = () => {
    if (!isEqual(initialValues, values)) {
      if (confirm('Are you sure you want to leave without saving changes?')) {
        navigate(-1)
      }
    } else {
      navigate(-1)
    }
  }

  const handleChange = fields => setValues({ ...values, ...fields })

  const handleChangeNotes = newNote => {
    const noteExists = !!notes.find(note => note.id === newNote.id)
    const newNotes = noteExists
      ? notes.map(note => (note.id === newNote.id ? newNote : note))
      : [...notes, newNote]
    setNotes(newNotes)
  }

  const handleSubmit = async () => {
    const validErrors = validator(values)
    setFormError(validErrors || {})
    if (!validErrors) {
      setIsSaving(true)
      try {
        const { ...otherFields } = formatBody(values)

        const response = await updateAppointment({
          id: appointment.id,
          ...otherFields,
          patientId: patient?.id,
        })

        if (response.errors) {
          onOpenSnackbar(
            'Unable to save appointment. Please check your input and try again.',
            'error'
          )
        } else {
          onOpenSnackbar('Updated appointment.')
        }

        const validNotes = notes.filter(
          note => !(!note.id && note.value === '')
        )
        validNotes.forEach(note => saveNote(note, appointment.id))

        if (location.state?.onEdit) {
          location.state.onEdit()
        }

        navigate(`/v2/appointment/${appointment.id}`, {
          state: {
            initialValues: { appointment: { ...response } },
          },
        })

        if (window.analytics && window.analytics.track) {
          window.analytics.track('Updated appointment', {
            category: 'Clearworks',
            label: 'Appointment updated by staff',
            datetime: response.startsAt,
            appointmentType: response.appointmentType.title,
            room: response.room?.title,
          })
        }
      } catch (e) {
        console.error('handleSubmit error - appointment edit', e)
        onOpenSnackbar('Unable to save appointment. Please try again.', 'error')
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleDelete = () => {
    try {
      deleteAppointment()

      const type = types.find(({ id }) => id === initialValues.typeId)
      const room = rooms.find(({ id }) => id === initialValues.roomId)

      if (window.analytics && window.analytics.track) {
        window.analytics.track('Deleted appointment', {
          category: 'Clearworks',
          label: 'Appointment deleted by staff',
          datetime: values.startsAtDate,
          appointmentType: type?.title,
          room: room?.title,
        })
      }
    } finally {
      if (location.state?.onCancel) {
        location.state.onCancel()
      }
      navigate('/v2/calendar')
      onOpenSnackbar('Appointment canceled.')
    }
  }

  const handleCancelAppointment = () =>
    confirm('Are you sure you want to cancel this appointment?') &&
    handleDelete()

  if (error) {
    return (
      <Container>
        <Header>
          <Typography variant="h2" fontSize="16px" fontWeight="500">
            Edit Appointment
          </Typography>
          <IconButton onClick={() => navigate(-1)} size="small">
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <Typography textAlign="center">
            Unable to load appointment details. Please refresh and try again.
          </Typography>
        </Content>
      </Container>
    )
  }

  return (
    <Container>
      <RTAppointments appointmentId={id} />
      <Header>
        <Typography variant="h2" fontSize="16px" fontWeight="500">
          Edit Appointment
        </Typography>
        <IconButton onClick={handleBack} size="small">
          <CloseIcon />
        </IconButton>
      </Header>

      <Content>
        {isLoading || loading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 40 }}
          >
            <CircularProgress />
          </div>
        ) : (
          <BaseForm
            edit
            error={formError}
            values={values}
            types={filteredTypes}
            rooms={rooms}
            doctors={doctors}
            onChange={handleChange}
            onChangeNotes={notes => handleChangeNotes(notes)}
          />
        )}
      </Content>

      <Footer>
        <Button
          variant="text"
          onClick={handleCancelAppointment}
          disabled={isLoading || loading || isSaving}
          size="large"
          sx={{ color: '#323232' }}
        >
          Cancel Appointment
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={isLoading || loading || isSaving}
          size="large"
        >
          {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </Footer>
    </Container>
  )
}

export default MobileAppointmentEdit
